/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { AlertStatus, IAlert } from '../../../../../../../shared/ui/src/lib/components/alert/alert-types';

@Component({
  selector: 'sysque-gauge-take-off-service',
  templateUrl: './sysque-gauge-take-off-service.component.html',
  styleUrls: ['./sysque-gauge-take-off-service.component.scss'],
  standalone: false
})
/**
 * Component responsible for handling the gauge take-off service functionality.
 * It provides methods to apply changes, validate input, show/hide alerts, and manage dialog dimensions.
 *
 */
export class SysqueGaugeTakeOffServiceComponent {
  @Input() takeOffLengthHeader: string = '';
  @Output() inputTakeOffLength = new EventEmitter<string>();
  @Output() closeWindow = new EventEmitter<void>();
  @ViewChild('dialog') dialog!: ElementRef;
  takeOffLength: string = '';
  public alertStatus = AlertStatus.Warning;
  public warningMessage = '';
  public isAlertVisible = false;

  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  private readonly alertHeight = 260;
  private readonly alertWidth = 350;
  private readonly withoutAlertHeight = 190;
  private readonly withoutAlertWidth = 285;

  /**
   * Applies the changes by validating the service type name and emitting the take-off length.
   *
   * @remarks
   * This method first validates the service type name using the `validateServiceTypeName` method.
   * If the validation is successful, it emits the `takeOffLength` value through the `inputTakeOffLength` event emitter.
   */
  public applyChanges(): void {
    if (this.validateServiceTypeName()) {
      this.inputTakeOffLength.emit(this.takeOffLength);
    }
  }

  /**
   * Validates the service type name by checking if the take-off length is not empty.
   * If the take-off length is empty, an alert is shown with a warning message.
   *
   * @returns {boolean} - Returns `true` if the take-off length is not empty, otherwise `false`.
   */
  public validateServiceTypeName(): boolean {
    const isValid = this.takeOffLength.trim() !== '';
    if (!isValid) {
      this.showAlert('Take Off length cannot be empty.', AlertStatus.Warning);
    }
    return isValid;
  }

  /**
   * Closes the dialog window by emitting the closeWindow event.
   *
   * @public
   * @returns {void}
   */
  public closeDialog(): void {
    this.closeWindow.emit();
  }

  /**
   * Hides the alert by resetting the dialog dimensions to the specified
   * height and width without the alert, clearing the warning message,
   * and setting the alert visibility to false.
   */
  public hideAlert(): void {
    this.setDialogDimensions(this.withoutAlertHeight, this.withoutAlertWidth);
    this.warningMessage = '';
    this.isAlertVisible = false;
  }

  /**
   * Displays an alert with the specified message and status.
   *
   * @param message - The message to display in the alert.
   * @param alertStatus - The status of the alert, indicating its type or severity.
   * @returns void
   */
  public showAlert(message: string, alertStatus: AlertStatus): void {
    this.setDialogDimensions(this.alertHeight, this.alertWidth);
    this.warningMessage = message;
    this.alertStatus = alertStatus;
    this.isAlertVisible = true;
  }

  /**
   * Sets the dimensions of the dialog element.
   *
   * @param height - The height to set for the dialog. Can be a number or a string.
   * @param width - The width to set for the dialog. Can be a number or a string.
   */
  private setDialogDimensions(height: number | string, width: number | string): void {
    this.dialog.nativeElement.style.height = `${height}px`;
    this.dialog.nativeElement.style.width = `${width}px`;
  }
}
